/**
 * Canonical aircraft silhouette data for spot reference point visualization.
 *
 * Each size category has a representative aircraft whose silhouette is displayed
 * in spots when reference points are configured. This helps users visualize
 * how aircraft will be positioned relative to reference points.
 *
 * Geometry data extracted from web.aircraft.geom (PostGIS polygons).
 * Coordinates are in a ~600x600 pixel space with nose pointing up (toward y=0).
 */

import { SizeCategory } from "./RampCanvasV2.types";

export type CanonicalAircraftGeom = {
  sizeCategory: SizeCategory;
  model: string;
  wingspan: number; // feet
  length: number; // feet
  coordinates: [number, number][]; // Polygon coordinates [x, y]
};

/**
 * Canonical aircraft geometry by size category.
 *
 * - very-small: Cessna 172 Skyhawk (high-wing prop)
 * - small: Citation CJ2 (light jet)
 * - midsize: Challenger 300 (mid-size jet)
 * - super-midsize: Gulfstream G650 (large cabin jet)
 * - large: Global 7500 (ultra-long range)
 */
export const CANONICAL_AIRCRAFT: Record<SizeCategory, CanonicalAircraftGeom> = {
  "very-small": {
    sizeCategory: "very-small",
    model: "Cessna 172 Skyhawk",
    wingspan: 36.08,
    length: 27.17,
    coordinates: [
      [301.5, 514.5],
      [304.5, 472.5],
      [311.518181818, 491.5],
      [313.5, 493.5],
      [316.5, 493.487179487],
      [390.5, 480.475609756],
      [392.476190476, 478.5],
      [393.475609756, 471.5],
      [393.458333333, 440.5],
      [389.5, 435.516949153],
      [311.5, 422.50990099],
      [330.5, 277.5],
      [448.5, 276.46875],
      [589.5, 257.487179487],
      [594.484375, 252.5],
      [598.388888889, 231.5],
      [600, 210.5],
      [596.479166667, 202.5],
      [590.5, 197.518181818],
      [580.5, 195.529411765],
      [439.5, 185.522222222],
      [364.5, 185.5],
      [364.5, 95.5],
      [307.5, 94.5],
      [305.5, 93.5],
      [301.5, 84.506329114],
      [297.506329114, 85.5],
      [293.5, 94.5],
      [235.5, 95.5],
      [235.5, 185.5],
      [160.5, 185.520833333],
      [19.5, 195.528571429],
      [7.5, 198.515873016],
      [0.53030303, 207.5],
      [0, 217.5],
      [4.51369863, 249.5],
      [5.523255814, 253.5],
      [9.5, 257.458333333],
      [151.5, 276.472972973],
      [267.5, 276.5],
      [269.5, 278.5],
      [288.511494253, 422.5],
      [212.5, 434.552631579],
      [208.5, 436.511627907],
      [206.516949153, 439.5],
      [206.519607843, 474.5],
      [208.5, 479.485074627],
      [213.5, 481.479591837],
      [285.5, 493.5],
      [288.475, 491.5],
      [295.5, 472.5],
      [298.5, 513.5],
      [299.5, 515.488636364],
      [301.5, 514.5],
    ],
  },
  small: {
    sizeCategory: "small",
    model: "Citation CJ2",
    wingspan: 49.83,
    length: 47.67,
    coordinates: [
      [424.5, 583.5],
      [423.5, 558.5],
      [309.5, 510.5],
      [322.5, 449.5],
      [345.5, 432.5],
      [347.5, 420.5],
      [343.5, 416.5],
      [343.5, 414.5],
      [356.5, 413.479591837],
      [361.5, 405.5],
      [365.5, 389.5],
      [367.5, 340.5],
      [372.5, 338.5],
      [595.5, 302.486666667],
      [598.5, 300.5],
      [597.5, 287.5],
      [600, 284.5],
      [597.5, 280.5],
      [597.5, 274.5],
      [595.5, 271.5],
      [590.5, 269.625],
      [333.5, 252.5],
      [333.5, 102.5],
      [331.5, 74.5],
      [325.5, 47.5],
      [316.5, 28.5],
      [306.5, 17.5],
      [302.5, 15.528571429],
      [295.5, 15.52],
      [290.5, 18.50390625],
      [283.509708738, 26.5],
      [275.516393443, 41.5],
      [267.510526316, 73.5],
      [265.51369863, 93.5],
      [265.5, 252.513333333],
      [8.5, 269.510752688],
      [3.5, 271.507633588],
      [1.509433962, 274.5],
      [1.5, 281.5],
      [0, 284.5],
      [1.5, 287.5],
      [1.5, 301.5],
      [6.5, 303.388888889],
      [231.5, 340.375],
      [233.525641026, 389.5],
      [237.525641026, 405.5],
      [242.5, 413.477777778],
      [255.5, 413.5],
      [255.5, 417.5],
      [251.545454545, 420.5],
      [254.523255814, 433.5],
      [276.5, 450.470588235],
      [284.533333333, 489.5],
      [290.5, 509.5],
      [176.5, 557.51369863],
      [174.511904762, 559.5],
      [175.5, 584.489361702],
      [295.5, 564.489690722],
      [296.515873016, 569.5],
      [298.5, 571.5],
      [302.5, 569.5],
      [303.5, 564.5],
      [418.5, 584.472222222],
      [424.5, 583.5],
    ],
  },
  midsize: {
    sizeCategory: "midsize",
    model: "Challenger 300",
    wingspan: 63.83,
    length: 68.75,
    coordinates: [
      [403.5, 599.5],
      [403.5, 573.5],
      [401.5, 571.509615385],
      [309.5, 509.5],
      [318.5, 466.5],
      [336.5, 456.5],
      [338.5, 459.5],
      [358.493377483, 458.5],
      [363.488372093, 439.5],
      [368.479166667, 410.5],
      [370.493710692, 377.5],
      [368.480769231, 356.5],
      [364.5, 351.5],
      [338.5, 351.523809524],
      [335.518518519, 354.5],
      [333.5, 361.5],
      [330.5, 361.5],
      [334.5, 330.5],
      [351.5, 329.5],
      [383.5, 338.5],
      [384.511904762, 352.5],
      [386.5, 355.493548387],
      [389.493243243, 354.5],
      [392.5, 341.493243243],
      [443.5, 355.5],
      [445.511627907, 369.5],
      [447.5, 371.5],
      [450.493902439, 369.5],
      [451.5, 359.5],
      [454.5, 358.5],
      [488.5, 368.493975904],
      [489.507407407, 377.5],
      [492.5, 382.5],
      [495.487179487, 379.5],
      [496.5, 370.5],
      [562.5, 389.494219653],
      [567.5, 394.484375],
      [570.5, 394.493377483],
      [577.5, 402.493006993],
      [579.492805755, 401.5],
      [579.491452991, 390.5],
      [571.484126984, 358.5],
      [564.5, 350.507633588],
      [334.5, 217.5],
      [334.483606557, 92.5],
      [331.480769231, 73.5],
      [325.489473684, 47.5],
      [316.481818182, 19.5],
      [310.490654206, 7.5],
      [304.5, 0.506993007],
      [296.5, 0],
      [289.50729927, 7.5],
      [281.508849558, 24.5],
      [271.509615385, 58.5],
      [265.510309278, 89.5],
      [265.5, 217.5],
      [35.5, 350.507633588],
      [28.509259259, 358.5],
      [20.509009009, 389.5],
      [20.510204082, 402.5],
      [21.5, 403.489795918],
      [29.493902439, 394.5],
      [33.5, 393.493377483],
      [39.5, 388.5],
      [103.5, 370.5],
      [104.512820513, 379.5],
      [107.5, 382.492366412],
      [110.492857143, 377.5],
      [111.492424242, 368.5],
      [145.5, 358.5],
      [148.5, 359.5],
      [148.507092199, 366.5],
      [151.5, 371.5],
      [154.488636364, 369.5],
      [156.5, 355.5],
      [205.5, 341.5],
      [208.5, 341.5],
      [210.507092199, 354.5],
      [213.5, 355.493589744],
      [215.488372093, 352.5],
      [216.5, 338.5],
      [247.5, 329.5],
      [265.5, 330.5],
      [269.5, 359.5],
      [268.5, 362.5],
      [266.5, 361.5],
      [264.477272727, 354.5],
      [261.5, 351.523809524],
      [235.5, 351.5],
      [231.519607843, 356.5],
      [229.535714286, 366.5],
      [229.516949153, 390.5],
      [232.545454545, 418.5],
      [238.525641026, 449.5],
      [242.5, 459.5],
      [260.5, 459.5],
      [263.5, 456.5],
      [281.5, 466.5],
      [287.514925373, 499.5],
      [290.5, 508.5],
      [197.509433962, 572.5],
      [196.5, 599.5],
      [201.5, 600],
      [294.5, 571.5],
      [299.5, 595.484126984],
      [301.486666667, 594.5],
      [304.5, 571.5],
      [393.5, 598.471428571],
      [403.5, 599.5],
    ],
  },
  "super-midsize": {
    sizeCategory: "super-midsize",
    model: "Gulfstream G650",
    wingspan: 99.58,
    length: 99.75,
    coordinates: [
      [411.409090909, 599.5],
      [411.4375, 575.5],
      [408.357142857, 567.5],
      [308.5, 496.5],
      [312.5, 482.5],
      [333.388888889, 481.5],
      [333.5, 468.5],
      [353.333333333, 467.5],
      [362.4, 419.5],
      [363.428571429, 379.5],
      [361.5, 371.5],
      [362.433333333, 370.5],
      [360.5, 366.5],
      [361.388888889, 365.5],
      [359.5, 362.5],
      [331.5, 362.5],
      [328.5, 368.5],
      [326.5, 368.5],
      [329.5, 332.5],
      [339.5, 332.5],
      [345.5, 334.5],
      [571.5, 418.5],
      [581.5, 425.5],
      [598.5, 450.5],
      [599.423076923, 436.5],
      [582.333333333, 394.5],
      [574.5, 384.566666667],
      [486.5, 319.5],
      [328.5, 189.5],
      [328.447368421, 94.5],
      [325.375, 68.5],
      [318.388888889, 37.5],
      [313.388888889, 22.5],
      [304.444444444, 3.5],
      [301.5, 0.5],
      [298.5, 0],
      [291.642857143, 10.5],
      [283.576923077, 31.5],
      [273.558823529, 75.5],
      [271.576923077, 93.5],
      [270.5, 189.5],
      [108.5, 323.5],
      [25.5, 384.552631579],
      [18.625, 392.5],
      [1.611111111, 432.5],
      [0.566666667, 436.5],
      [1.5, 449.5],
      [3.5, 448.5],
      [18.5, 425.5],
      [28.5, 418.5],
      [251.5, 335.5],
      [260.5, 332.5],
      [270.5, 332.5],
      [272.5, 340.5],
      [271.566666667, 349.5],
      [273.5, 368.5],
      [271.5, 368.5],
      [268.5, 362.5],
      [240.5, 362.5],
      [239, 364.5],
      [236.571428571, 375.5],
      [237.5, 381.5],
      [235.571428571, 392.5],
      [237.5, 407.5],
      [236.566666667, 412.5],
      [238.5, 418.5],
      [237.642857143, 421.5],
      [239.5, 425.5],
      [239.625, 434.5],
      [241.5, 437.5],
      [241.5625, 445.5],
      [243.7, 455.5],
      [245.5, 457.5],
      [244.535714286, 458.5],
      [246.666666667, 467.5],
      [265.5, 468.5],
      [267.5, 482.388888889],
      [287.5, 482.5],
      [291.5, 496.5],
      [191.666666667, 567.5],
      [188.590909091, 573.5],
      [188.611111111, 599.5],
      [196.5, 598.409090909],
      [296.5, 562.433333333],
      [299.5, 578.5],
      [301.441176471, 577.5],
      [302.5, 564.5],
      [304.5, 562.5],
      [400.5, 597.388888889],
      [408.5, 600],
      [411.409090909, 599.5],
    ],
  },
  large: {
    sizeCategory: "large",
    model: "Global 7500",
    wingspan: 104,
    length: 111,
    coordinates: [
      [391.5, 599.5],
      [391.423076923, 574.5],
      [387.433333333, 567.5],
      [309.5, 505.529411765],
      [314.452380952, 489.5],
      [326.5, 483.452380952],
      [328.5, 479.5],
      [347.5, 479.466666667],
      [350.47826087, 477.5],
      [356.462962963, 443.5],
      [357.479166667, 400.5],
      [355.472222222, 392.5],
      [353.5, 390.5],
      [329.5, 390.5],
      [326.520833333, 392.5],
      [325.5, 395.5],
      [320.5, 394.5],
      [325.409090909, 362.5],
      [325.45, 338.5],
      [394.5, 340.5],
      [552.5, 416.5],
      [556.5, 421.5],
      [561.5, 422.481818182],
      [578.5, 439.464285714],
      [581.47826087, 438.5],
      [581.476190476, 430.5],
      [559.454545455, 394.5],
      [552.5, 386.558823529],
      [325.5, 206.552631579],
      [324.454545455, 54.5],
      [321.357142857, 38.5],
      [313.416666667, 16.5],
      [304.478723404, 2.5],
      [298.5, 0],
      [293.558823529, 4.5],
      [284.545454545, 20.5],
      [278.552631579, 37.5],
      [275.5625, 51.5],
      [274.534482759, 206.5],
      [48.5, 385.6],
      [42.555555556, 391.5],
      [20.5625, 426.5],
      [18.523809524, 430.5],
      [18.521276596, 438.5],
      [20.5, 440.475],
      [38.5, 422.5],
      [44.5, 420.5],
      [47.5, 416.5],
      [205.5, 340.5],
      [274.5, 338.470588235],
      [274.6, 365.5],
      [279.5, 394.5],
      [274.5, 395.5],
      [272.5, 390.511904762],
      [246.5, 390.518867925],
      [244.532258065, 392.5],
      [243.525641026, 441.5],
      [249.527027027, 476.5],
      [252.5, 479.467741935],
      [271.5, 479.5],
      [273.5, 483.447368421],
      [285.5, 489.5],
      [290.5, 505.5],
      [213.516393443, 566.5],
      [208.529411765, 575.5],
      [208.5, 599.5],
      [212.5, 599.416666667],
      [295.456521739, 561.5],
      [297.5, 561.5],
      [299.5, 567.5],
      [302.452380952, 565.5],
      [303.5, 561.458333333],
      [387.5, 599.333333333],
      [391.5, 599.5],
    ],
  },
};

/**
 * Get the canonical aircraft for a spot based on its width in feet.
 */
export function getCanonicalAircraftForSpot(
  spotWidthFeet: number
): CanonicalAircraftGeom {
  if (spotWidthFeet <= 40) return CANONICAL_AIRCRAFT["very-small"];
  if (spotWidthFeet <= 60) return CANONICAL_AIRCRAFT["small"];
  if (spotWidthFeet <= 80) return CANONICAL_AIRCRAFT["midsize"];
  if (spotWidthFeet <= 100) return CANONICAL_AIRCRAFT["super-midsize"];
  return CANONICAL_AIRCRAFT["large"];
}

/**
 * Get the bounding box of an aircraft's geometry coordinates.
 */
export function getAircraftBounds(coordinates: [number, number][]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
} {
  const xs = coordinates.map((c) => c[0]);
  const ys = coordinates.map((c) => c[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
}
